import { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronDown, AlertCircle, ArrowRight, Banknote, Clock, Landmark, TrendingDown, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import LeadForm from "@/forms/engine/LeadForm";
import toolCostOfCash, { costOfCashContext } from "@/forms/definitions/toolCostOfCash";
import { useToolLifecycle } from "@/utils/trackingHooks";

const COST_OF_CASH_ITEM_NAME = "cost_of_cash_calculator";
const COST_OF_CASH_ITEM_OPTS = { itemType: "calculator" };

const WEEKS_PER_MONTH = 4.33;
const MONTHS_PER_YEAR = 12;

const DEFAULTS = {
  timePerTrip: 30,
  hourlyWage: 12,
  daysOpen: 6,
  countingMinutes: 15,
  depositFee: 0.5,
  cashLoss: 0.5,
};

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(val);
}

function formatHours(val) {
  const rounded = Math.round(val * 10) / 10;
  return `${rounded} hr${rounded !== 1 ? "s" : ""}`;
}

function parseInputs(inputs) {
  return {
    weeklyCash: parseFloat(inputs.weeklyCash) || 0,
    bankTrips: parseFloat(inputs.bankTrips) || 0,
    timePerTrip: parseFloat(inputs.timePerTrip) || DEFAULTS.timePerTrip,
    hourlyWage: parseFloat(inputs.hourlyWage) || DEFAULTS.hourlyWage,
    countingMinutes: parseFloat(inputs.countingMinutes) || DEFAULTS.countingMinutes,
    daysOpen: parseFloat(inputs.daysOpen) || DEFAULTS.daysOpen,
    depositFee: (parseFloat(inputs.depositFee) || DEFAULTS.depositFee) / 100,
    cashLoss: (parseFloat(inputs.cashLoss) || DEFAULTS.cashLoss) / 100,
  };
}

function calculateBankingHours(parsed) {
  const weeklyHours = (parsed.bankTrips * parsed.timePerTrip) / 60;
  return weeklyHours * WEEKS_PER_MONTH;
}

function calculateCountingHours(parsed) {
  const weeklyHours = (parsed.countingMinutes * parsed.daysOpen) / 60;
  return weeklyHours * WEEKS_PER_MONTH;
}

function calculateCosts(inputs) {
  const parsed = parseInputs(inputs);
  if (parsed.weeklyCash <= 0 || parsed.bankTrips <= 0) return null;

  const monthlyCash = parsed.weeklyCash * WEEKS_PER_MONTH;
  const monthlyBankingHours = calculateBankingHours(parsed);
  const monthlyCountingHours = calculateCountingHours(parsed);
  const totalMonthlyHours = monthlyBankingHours + monthlyCountingHours;

  const labourCost = totalMonthlyHours * parsed.hourlyWage;
  const bankingCost = monthlyCash * parsed.depositFee;
  const cashLoss = monthlyCash * parsed.cashLoss;
  const totalMonthlyCost = labourCost + bankingCost + cashLoss;

  return {
    monthlyCost: totalMonthlyCost,
    annualCost: totalMonthlyCost * MONTHS_PER_YEAR,
    labourCost,
    bankingCost,
    cashLoss,
    totalHours: totalMonthlyHours,
    monthlyCash,
  };
}

function CurrencyInput({ label, name, value, onChange, prefix = "£", placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">{prefix}</span>}
        <input
          type="number"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          step="any"
          min="0"
          className={`w-full ${prefix ? "pl-8" : "pl-4"} pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function NumberInput({ label, name, value, onChange, placeholder, suffix, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        <input
          type="number"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          step="any"
          min="0"
          className={`w-full pl-4 ${suffix ? "pr-12" : "pr-4"} py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`}
          data-testid={testId}
        />
        {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

function PercentInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        <input
          type="number"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          step="any"
          min="0"
          max="100"
          className="w-full pl-4 pr-8 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all"
          data-testid={testId}
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">%</span>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, sublabel, variant = "default", testId }) {
  const bg = {
    default: "bg-white border border-brand-light-grey",
    highlight: "bg-brand-pink-light border border-brand-pink/20",
    annual: "bg-gradient-to-br from-brand-pink-light to-white border border-brand-pink/30",
  };
  const text = {
    default: "text-brand-black",
    highlight: "text-brand-pink",
    annual: "text-brand-pink",
  };
  return (
    <div className={`${bg[variant]} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-brand-grey" />}
        <p className="text-xs font-bold uppercase tracking-wider text-brand-grey">{label}</p>
      </div>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${text[variant]}`}>{value}</p>
      {sublabel && <p className="text-xs text-brand-grey mt-0.5">{sublabel}</p>}
    </div>
  );
}

function ResultsPanel({ results }) {
  return (
    <div className="space-y-4 pt-2">
      <div className="bg-brand-pink-light border border-brand-pink/20 rounded-xl p-5 text-center">
        <p className="text-sm font-semibold text-brand-black mb-1">Cash could be costing your business</p>
        <p className="text-3xl sm:text-4xl font-heading font-bold text-brand-pink" data-testid="cash-annual-headline">{formatGBP(results.annualCost)}</p>
        <p className="text-sm text-brand-grey mt-1">per year</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ResultCard label="Monthly cost" value={formatGBP(results.monthlyCost)} variant="highlight" testId="cash-monthly-cost" />
        <ResultCard label="Annual cost" value={formatGBP(results.annualCost)} variant="annual" testId="cash-annual-cost" />
      </div>

      <p className="text-xs font-bold uppercase tracking-wider text-brand-grey pt-1">Cost breakdown</p>
      <div className="grid grid-cols-2 gap-3">
        <ResultCard icon={Clock} label="Labour cost" value={formatGBP(results.labourCost)} sublabel={`${formatHours(results.totalHours)} / month`} testId="cash-labour-cost" />
        <ResultCard icon={Landmark} label="Banking fees" value={formatGBP(results.bankingCost)} testId="cash-banking-cost" />
        <ResultCard icon={TrendingDown} label="Estimated losses" value={formatGBP(results.cashLoss)} testId="cash-loss-cost" />
        <ResultCard icon={Clock} label="Time on cash" value={formatHours(results.totalHours)} sublabel="per month" testId="cash-total-hours" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link
          to="/switch-and-save"
          className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine"
          data-testid="cash-cta-compare"
        >
          Compare this with card costs <ArrowRight size={16} />
        </Link>
        <Link
          to="/contact"
          className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors"
          data-testid="cash-cta-review"
        >
          Get a tailored savings review
        </Link>
      </div>
    </div>
  );
}

function CashInputs({ inputs, handleChange, showAdvanced, setShowAdvanced }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CurrencyInput label="Weekly cash takings" name="weeklyCash" value={inputs.weeklyCash} onChange={handleChange} placeholder="e.g. 3000" testId="cash-weekly-takings" />
        <NumberInput label="Bank trips per week" name="bankTrips" value={inputs.bankTrips} onChange={handleChange} placeholder="e.g. 3" testId="cash-bank-trips" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumberInput label="Time per bank trip" name="timePerTrip" value={inputs.timePerTrip} onChange={handleChange} placeholder="30" suffix="mins" testId="cash-time-per-trip" />
        <CurrencyInput label="Staff hourly wage" name="hourlyWage" value={inputs.hourlyWage} onChange={handleChange} placeholder="12" testId="cash-hourly-wage" />
      </div>
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors"
        data-testid="cash-advanced-toggle"
      >
        Advanced options
        <ChevronDown size={16} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
      </button>
      {showAdvanced && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4 border border-brand-light-grey/50">
          <NumberInput label="Cash counting time per day" name="countingMinutes" value={inputs.countingMinutes} onChange={handleChange} placeholder="15" suffix="mins" testId="cash-counting-time" />
          <NumberInput label="Days open per week" name="daysOpen" value={inputs.daysOpen} onChange={handleChange} placeholder="6" testId="cash-days-open" />
          <PercentInput label="Bank deposit fee" name="depositFee" value={inputs.depositFee} onChange={handleChange} placeholder="0.5" testId="cash-deposit-fee" />
          <PercentInput label="Estimated cash losses" name="cashLoss" value={inputs.cashLoss} onChange={handleChange} placeholder="0.5" testId="cash-loss-pct" />
        </div>
      )}
    </>
  );
}

function useCashCalculator(inputs) {
  return useMemo(() => calculateCosts(inputs), [inputs]);
}

function CaptureSuccess() {
  return (
    <div className="text-center py-6" data-testid="cash-form-success">
      <CheckCircle size={32} className="text-brand-aqua mx-auto mb-3" />
      <h4 className="font-heading text-lg font-bold text-brand-black mb-1">Thank you</h4>
      <p className="text-sm text-brand-grey">We&apos;ll be in touch with your personalised review shortly.</p>
    </div>
  );
}

export default function CostOfCashCalculator() {
  const [inputs, setInputs] = useState({
    weeklyCash: "", bankTrips: "", timePerTrip: "", hourlyWage: "",
    countingMinutes: "", daysOpen: "", depositFee: "", cashLoss: "",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { markStarted, markCompleted } = useToolLifecycle(COST_OF_CASH_ITEM_NAME, COST_OF_CASH_ITEM_OPTS);
  const results = useCashCalculator(inputs);
  const leadContext = useMemo(() => costOfCashContext(inputs, results, DEFAULTS), [inputs, results]);
  const handleFormSubmit = useCallback(() => setFormSubmitted(true), []);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  }, [markStarted]);

  // Completion means a real calculated result, not an input change. Values,
  // result totals and elapsed time stay inside the component and form handoff.
  useEffect(() => {
    if (results) markCompleted();
  }, [results, markCompleted]);

  const hasMinInput = Boolean(results);

  return (
    <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden" data-testid="cost-of-cash-calculator">
      <div className="bg-brand-pink-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-pink/10">
        <div className="w-10 h-10 rounded-xl bg-brand-pink flex items-center justify-center">
          <Banknote size={20} className="text-white" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Cost of Cash Calculator</h3>
          <p className="text-xs text-brand-grey">Discover the hidden cost of handling cash</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-6">
        <CashInputs inputs={inputs} handleChange={handleChange} showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced} />

        {!hasMinInput && (
          <div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-brand-grey" />
            Enter your weekly cash takings and number of bank trips to see your estimated cost of cash.
          </div>
        )}

        {results && <ResultsPanel results={results} />}
        {results && (
          <div className="border-t border-brand-light-grey/60 pt-6">
            <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
            <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
            {formSubmitted ? (
              <CaptureSuccess />
            ) : (
              <LeadForm schema={toolCostOfCash} context={leadContext} onSuccess={handleFormSubmit} variant="compact" />
            )}
          </div>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          These estimates are illustrative and based on the information you&apos;ve entered. Actual costs depend on your business operations, banking arrangements and staffing. Contact us for a personalised review.
        </p>
      </div>
    </div>
  );
}
